import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, NgZone, signal, ViewChild, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { ChartHandlerV2 } from 'src/app/classes/widget/chartHandlerV2';
import { ForecastNoteComponent } from 'src/app/components/project-pages/dashboard/forecast-note/forecast-note.component';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  imports: [
    ForecastNoteComponent,
    WidgetLoadingComponent,
    WidgetTitleComponent,
    CommonModule,
    TranslateModule
  ],
  standalone: true,
  styleUrls: ['./chart.component.scss']
})
export class ChartComponent extends WidgetDirective {
  // Services spécifiques à ChartComponent
  private router: Router = inject(Router);
  protected translate: TranslateService = inject(TranslateService);

  // Component API spécifique à ChartComponent
  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef;
  @ViewChild('chartContent', { static: false }) chartContent!: ElementRef;

  // Variables spécifiques à ChartComponent
  version: number = 0;
  edited: boolean = false;
  containerStyle: string = "";
  blockStyle: string = "width:100%;height:100%;display:flex;flex-direction: column;justify-content: center;align-items: center;";
  titleStyle: string = "font-size:16px;margin:0px 20px 20px;";
  valueStyle: string = "font-size:30px;font-weight:bold;margin:10px 0;";
  titlePosition: string = "top";
  // Chart.js runs outside Angular (see ChartHandlerV2).
  chartHandler: ChartHandlerV2 = new ChartHandlerV2(inject(NgZone));
  chartIsReady: boolean = false;
  ready: boolean = false;
  comp: number = parseInt((Math.random() * 100).toString());
  showEdit: boolean = false;
  private dataLoaded: boolean = false;

  private tempWidgetData: any = null; // Données temporaires (zoom)
  private tempWidgetQuery: any = null; // Query temporaire (zoom)
  private zoomHistory: any[] = [];

  // Signals
  isZoomed: WritableSignal<boolean> = signal(false);

  override ngOnInit(): void {
    this.resetStyles();

    if (!this.dataLoaded && this.widget) {
      this.updateData();
      this.dataLoaded = true;
    }

    if (this.chartHandler) {
      this.chartHandler.setRouter(this.router);
      this.chartHandler.forecastTexts = {
        today: this.translate.instant('prediction.today'),
        range: this.translate.instant('prediction.range', { lower: '{lower}', upper: '{upper}' }),
        target: this.translate.instant('prediction.target', { value: '{value}' }),
        reached: this.translate.instant('prediction.target_reached', { value: '{value}', date: '{date}' }),
      };
    }
  }

  override ngOnDestroy(): void {
    this.widgetQuery = null;
    // Chart.js registers a MutationObserver watching the whole `document` (to detect its own
    // canvas being detached/reattached) plus a shared `window` resize listener, neither ever
    // released unless `Chart.destroy()` runs - without this, every chart widget ever shown
    // leaks a permanent, session-lifetime observer that re-scans every DOM mutation across the
    // ENTIRE app forever, not just this dashboard. Harmless individually, but compounds badly:
    // enough of them left over from browsing dashboards made an unrelated page (Devices >
    // Modèles de configuration's "vue globale", which inserts many nested nodes at once) freeze
    // the whole tab, since each leaked observer re-scans that entire insertion for nothing.
    this.chartHandler.destroy();
    super.ngOnDestroy();
  }

  // Implémentation de la méthode abstraite
  init(): void {
    if (!this.widget || !this.widget.visible || this.resize) return;

    this.widgetDesign = this.widget.design;
    this.widgetData = this.widget.data;
    this.widgetQuery = this.widget.query;

    if (this.type !== this.widget.type) {
      this.resetStyles();
    }

    this.type = this.widget.type;
    this.initOptions();

    if (!this.chartIsReady) {
      const initInt = setInterval(() => {
        if (this.chartCanvas) {
          clearInterval(initInt);
          this.initChart();
          this.updateChart();
        }
      }, 50);
    } else {
      this.updateChart();
    }
  }

  resetStyles(): void {
    this.blockStyle = "width:100%;display:flex;flex-direction: column;align-content:center;";
    this.titleStyle = "font-size:16px;margin:0px 20px 20px;";
    this.valueStyle = "font-size:30px;font-weight:bold;margin:10px 0;line-height:1em;";
    this.containerStyle = "";
  }

  initChart(): void {
    try {
      if (!this.widget) return;
      if (!this.chartCanvas) return;

      this.chartHandler.setGraphType(this.widget.graphType ?? 'line');
      this.chartHandler.setChartCtx(this.chartCanvas.nativeElement);

      this.chartHandler.setZoomCallback((newQuery: any) => {
        this.sendZoomQuery(newQuery);
      });

      this.chartIsReady = true;
    } catch (e) {
      console.error(e);
    }
  }

  initOptions(): void {
    try {
      this.resetStyles();
      const options = this.widgetDesign;

      if (!options) return;

      this.containerStyle = `background-color: var(--background-color);`;
      if (options.title) {
        this.showTitle = options.title.display == undefined ? true : options.title.display;
        this.titlePosition = options.title.position || 'top';
        this.titleStyle = `font-size:${options.title.fontSize || '16'}px; margin:10px 20px;text-align:${options.title.align || 'center'};`;
      }
    } catch (e) {
      console.error(e);
    }
  }

  updateChart(): void {
    if (!this.widget) return;

    this.chartHandler.setGraphType(this.widget.graphType ?? 'line');

    let dataToDisplay = this.isZoomed() && this.tempWidgetData ? this.tempWidgetData : this.widgetData;
    let queryToUse = this.isZoomed() && this.tempWidgetQuery ? this.tempWidgetQuery : this.widgetQuery;

    // if (this.widgetData && this.widget.dataset && this.widget.dataset.enable) {
    //   this.widget.dataset.data.forEach((d: { s: any; }) => d.s = this.widget.dataset.label);
    //   this.widgetData = this.widgetData.concat(this.widget.dataset.data);
    // }
    if (dataToDisplay && this.widget.dataset && this.widget.dataset.enable) {
      this.widget.dataset.data.forEach((d: { s: any; }) => d.s = this.widget.dataset.label);
      dataToDisplay = dataToDisplay.concat(this.widget.dataset.data);
    }

    // this.chartHandler.updateChart(this.widgetData, this.widgetDesign, this.widgetQuery);
    this.chartHandler.updateChart(dataToDisplay, this.widgetDesign, queryToUse, this.galleryTool);

    this.widgetRendered = true;
  }

  // The base query; the chart is not ready until its answer, nothing is sent while its options are incomplete.
  override sendQuery(project: any = null): boolean {
    if (!this.widget.query || !this.validationGraphOption()) return false;
    this.chartIsReady = false;
    return super.sendQuery(project);
  }

  override validationGraphOption(): boolean {
    if (this.widget.graphOption && Array.isArray(this.widget.graphOption)) {
      const missingRequiredOptions = this.widget.graphOption.filter((option: any) => {
        if (!option.required) return false;

        const query = this.widget.query;

        switch (option.input) {
          case 'field':
            return !query.field || Object.keys(query.field).length === 0;
          case 'aggregation':
            return !query.aggregation || Object.keys(query.aggregation).length === 0;
          default:
            return false;
        }
      });

      if (missingRequiredOptions.length > 0) {
        this.error = `Configuration incomplète : ${missingRequiredOptions.map((opt: any) => opt.name).join(', ')}`;
        this.widgetData = null;
        this.widget.data = [];
        this.dataByProject = {};

        if (this.chartIsReady) {
          this.chartHandler.updateChart([], this.widgetDesign, this.widgetQuery, this.galleryTool);
        }

        return false;
      }
    }

    return true;
  }

  sendZoomQuery(newQuery: any): void {
    if (!newQuery) return;

    const isYAxisZoom = this.isZoomOnYAxis(newQuery);

    if (isYAxisZoom && this.zoomHistory.length > 0) {
      return;
    }

    this.zoomHistory.push({
      query: this.tempWidgetQuery || this.widgetQuery,
      data: this.tempWidgetData || this.widgetData
    });

    this.isZoomed.set(true);
    this.tempWidgetQuery = newQuery;

    const query = { ...newQuery };

    if (!query.filters) {
      query.filters = {};
    }

    if (!query.limit) {
      query.limit = 5000;
    }

    if (this.filters) {
      this.filters.forEach((filter: any) => {
        if (filter.enable) {
          query.filters[filter.name] = {
            options: filter.options,
            type: filter.type.toLowerCase()
          };
        }
      });
    }

    if (this.project) {
      query.key = this.project.accessKey;
      query.project = this.project.id;
    }

    query.id = this.id;
    query.collection = this.widget.category;

    const resHandler = (res: any[]) => {
      this.tempWidgetData = res;
      this.updateChart();
    };

    const errHandler = (err: any) => {
      console.error('Erreur lors du zoom:', err);
      this.isZoomed.set(false);
      this.tempWidgetData = null;
      this.tempWidgetQuery = null;
    };

    this.aggregate(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resHandler,
        error: errHandler
      });
  }

  zoomBack(): void {
    if (this.zoomHistory.length === 0) {
      this.resetZoom();
      return;
    }

    const previous = this.zoomHistory.pop();
    this.tempWidgetQuery = previous.query;
    this.tempWidgetData = previous.data;

    if (this.tempWidgetQuery === this.widgetQuery || !this.tempWidgetQuery) {
      this.isZoomed.set(false);
      this.zoomHistory = [];

      if (this.chartHandler) {
        this.chartHandler.resetZoomTracking();
      }
    }

    this.updateChart();
  }

  resetZoom(): void {
    this.isZoomed.set(false);
    this.tempWidgetData = null;
    this.tempWidgetQuery = null;
    this.zoomHistory = [];

    if (this.chartHandler) {
      this.chartHandler.resetZoomTracking();
    }

    this.updateChart();
  }

  private isZoomOnYAxis(query: any): boolean {
    const fieldKeys = query.field ? Object.keys(query.field) : [];
    const aggKeys = query.aggregation ? Object.keys(query.aggregation) : [];

    if (fieldKeys.length === 0 || aggKeys.length === 0) return false;

    const fieldKey = fieldKeys[0];
    const aggKey = aggKeys[0];

    return fieldKey === aggKey;
  }
}